import React from 'react';
import './AboutUs.css'; 

const AboutUs = () => {
  const teamMembers = [
    {
      id: 1,
      name: 'Alex Rivera',
      role: 'Founder & CEO',
      bio: 'Passionate language learner and educator with over 10 years of experience in language acquisition methodologies.',
      languages: ['English', 'Spanish', 'French'],
      avatar: 'A'
    },
    {
      id: 2,
      name: 'Maria Rodriguez',
      role: 'Head of Spanish Curriculum',
      bio: 'Native Spanish speaker with a PhD in Linguistics and specialized training in second language acquisition.',
      languages: ['Spanish', 'English', 'Portuguese'],
      avatar: 'M'
    },
    {
      id: 3,
      name: 'Jean Dupont',
      role: 'Head of French Curriculum',
      bio: 'Former university professor with expertise in French literature and language teaching methodologies.',
      languages: ['French', 'English', 'Italian'],
      avatar: 'J'
    },
    {
      id: 4,
      name: 'Hiroshi Tanaka',
      role: 'Head of Japanese Curriculum',
      bio: 'Certified language instructor with experience developing immersive learning programs for Japanese learners.',
      languages: ['Japanese', 'English', 'Mandarin'],
      avatar: 'H'
    }
  ];

  return (
    <div className="about-us-section">
      <h1>About VirtuLingu Club</h1>
      
      <div className="mission-vision">
        <div className="mission-card">
          <h2>Our Mission</h2>
          <p>
            At VirtuLingu Club, we're on a mission to revolutionize language learning by creating a vibrant community where learners connect, practice, and grow together. We believe that language acquisition happens best through meaningful interactions and connections with others.
          </p>
        </div>
        
        <div className="vision-card">
          <h2>Our Vision</h2>
          <p>
            We envision a world where language barriers no longer exist, where people from different cultures can communicate freely and build understanding. VirtuLingu Club aims to be the bridge that connects language learners with native speakers and fellow students worldwide.
          </p>
        </div>
      </div>
      
      <div className="connectivism-philosophy">
        <h2>Our Learning Philosophy</h2>
        <div className="philosophy-content">
          <div className="philosophy-image">
            <svg width="200" height="200" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <g fill="none" stroke="url(#gradient)" strokeWidth="2">
                <circle cx="100" cy="100" r="50" />
                <circle cx="50" cy="50" r="20" />
                <circle cx="150" cy="50" r="20" />
                <circle cx="50" cy="150" r="20" />
                <circle cx="150" cy="150" r="20" />
                <line x1="100" y1="100" x2="50" y2="50" />
                <line x1="100" y1="100" x2="150" y2="50" />
                <line x1="100" y1="100" x2="50" y2="150" />
                <line x1="100" y1="100" x2="150" y2="150" />
                <line x1="50" y1="50" x2="150" y2="50" />
                <line x1="50" y1="150" x2="150" y2="150" />
                <line x1="50" y1="50" x2="50" y2="150" />
                <line x1="150" y1="50" x2="150" y2="150" />
              </g>
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#8A2BE2" />
                  <stop offset="100%" stopColor="#FF1493" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <div className="philosophy-text">
            <h3>Connectivism: Learning Through Connections</h3>
            <p>
              VirtuLingu Club is built on the theory of connectivism, which recognizes that learning happens within networks of connections. In today's digital age, learning is not just an individual activity but a process of building and navigating networks.
            </p>
            <p>
              Our platform facilitates these connections by:
            </p>
            <ul>
              <li>Connecting learners with native speakers for authentic practice</li>
              <li>Creating study groups for collaborative learning</li>
              <li>Providing a community feed where knowledge is shared and discussed</li>
              <li>Offering structured courses that incorporate social learning elements</li>
            </ul>
            <p>
              By fostering these connections, we create a rich learning environment where knowledge flows naturally and learners are constantly exposed to authentic language use.
            </p>
          </div>
        </div>
      </div>
      
      <div className="team-section">
        <h2>Meet Our Team</h2>
        <div className="team-grid">
          {teamMembers.map(member => (
            <div className="team-card" key={member.id}>
              <div className="member-avatar" style={{ backgroundColor: `hsl(${member.id * 60}, 70%, 60%)` }}>
                {member.avatar}
              </div>
              <h3>{member.name}</h3>
              <p className="member-role">{member.role}</p>
              <p className="member-bio">{member.bio}</p>
              <div className="member-languages">
                <h4>Languages:</h4>
                <div className="language-tags">
                  {member.languages.map((language, index) => (
                    <span key={index} className="language-tag">{language}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="contact-section">
        <h2>Get in Touch</h2>
        <div className="contact-cards">
          <div className="contact-card">
            <div className="contact-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
            </div>
            <h3>Email Us</h3>
            <p>support@virtulingu.club</p>
            <p>partnerships@virtulingu.club</p>
          </div>
          
          <div className="contact-card">
            <div className="contact-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15.05 5A5 5 0 0 1 19 8.95M15.05 1A9 9 0 0 1 23 8.94m-1 7.98v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
            </div>
            <h3>Call Us</h3>
            <p>+1 (555) 123-4567</p>
            <p>Monday - Friday: 9am - 5pm EST</p>
          </div>
          
          <div className="contact-card">
            <div className="contact-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
            </div>
            <h3>Visit Us</h3>
            <p>123 Language Lane</p>
            <p>San Francisco, CA 94103</p>
          </div>
        </div>
      </div>
      
      <div className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-list">
          <div className="faq-item">
            <h3>How does VirtuLingu Club differ from other language learning platforms?</h3>
            <p>
              Unlike traditional platforms that focus solely on lessons and exercises, VirtuLingu Club integrates social learning at its core. We combine structured courses with a vibrant community where you can practice with native speakers, join study groups, and build lasting connections with fellow learners.
            </p>
          </div>
          
          <div className="faq-item">
            <h3>Do I need to be at a certain level to join?</h3>
            <p>
              Not at all! VirtuLingu Club welcomes learners at all levels, from complete beginners to advanced speakers. Our platform will match you with appropriate courses, study groups, and language partners based on your current level.
            </p>
          </div>
          
          <div className="faq-item">
            <h3>How can I become a language tutor on the platform?</h3>
            <p>
              If you're a native or fluent speaker interested in tutoring, you can apply through the "Become a Tutor" section in your profile. We'll review your application, and if approved, you can start offering tutoring services to our community.
            </p>
          </div>
          
          <div className="faq-item">
            <h3>Is VirtuLingu Club free to use?</h3>
            <p>
              We offer both free and premium tiers. The free tier gives you access to the community features and basic courses, while our premium subscription unlocks advanced courses, unlimited language exchange sessions, and additional learning resources.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// At the end of the file, change:
export default AboutUs; // Changed from AboutUsSection
